import { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { CircularProgress } from '@mui/material';

interface HtmlViewerProps {
  fileUrl: string;
}

const HtmlZipViewer = ({ fileUrl }: HtmlViewerProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const virtualFsRef = useRef<Record<string, string>>({});
  const isMountedRef = useRef(false);

  // CSS para ocultar elementos específicos
  const hideElementsCSS = `
    #wpadminbar,
    [data-elementor-id="7464"],
    [data-elementor-type="header"] {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      width: 0 !important;
      opacity: 0 !important;
      position: absolute !important;
    }
  `;

  useEffect(() => {
    isMountedRef.current = true;
    const abortController = new AbortController();

    const loadZipAndCreateVirtualFs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 1. Descargar el ZIP
        const response = await fetch(fileUrl, { 
          signal: abortController.signal,
          cache: 'force-cache'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const zipBlob = await response.blob();

        // 2. Cargar ZIP y crear sistema de archivos virtual
        const zip = new JSZip();
        const loadedZip = await zip.loadAsync(zipBlob);
        const newVirtualFs: Record<string, string> = {};

        await Promise.all(
          Object.values(loadedZip.files).map(async (file) => {
            if (!file.dir && isMountedRef.current) {
              try {
                const blob = await file.async('blob');
                newVirtualFs[file.name] = URL.createObjectURL(blob);
              } catch (err) {
                console.warn(`Error procesando archivo ${file.name}:`, err);
              }
            }
          })
        );

        if (!isMountedRef.current) return;
        virtualFsRef.current = newVirtualFs;

        // 3. Buscar archivo HTML principal
        const htmlFile = Object.values(loadedZip.files).find(
          (file) => !file.dir && /index\.html$/i.test(file.name)
        ) || Object.values(loadedZip.files).find(
          (file) => !file.dir && /\.html?$/i.test(file.name)
        );

        if (!htmlFile) throw new Error('No se encontró ningún archivo HTML');

        // 4. Procesar HTML con manejo correcto de assets
        let htmlContent = await htmlFile.async('text');
        
        // A. Inyectar CSS para ocultar elementos
        htmlContent = htmlContent.replace(/<head(.*?)>/i, `<head$1><style id="custom-hide-elements">${hideElementsCSS}</style>`);

        // B. Reemplazar solo rutas locales (ignorar absolutas)
        Object.entries(newVirtualFs).forEach(([fullPath, blobUrl]) => {
          const fileName = fullPath.split('/').pop();
          if (!fileName) return;

          // Crear expresión para coincidir con la ruta completa o solo el nombre del archivo
          // const pathParts = fullPath.split('/');
          const searchPatterns = [];
          
          // Patrón para la ruta completa
          searchPatterns.push(escapeRegExp(fullPath));
          
          // Patrón para el nombre del archivo al final de cualquier ruta
          searchPatterns.push(`[^'"]*${escapeRegExp(fileName)}`);

          searchPatterns.forEach(pattern => {
            // Reemplazar en src, href y url()
            htmlContent = htmlContent.replace(
              new RegExp(`(src|href)=["'](${pattern})["']`, 'gi'),
              (match, attr, originalPath) => {
                // Ignorar si es una URL absoluta (http://, https://, //)
                if (/^(https?:)?\/\//.test(originalPath)) {
                  return match;
                }
                return `${attr}="${blobUrl}"`;
              }
            );
            
            // Reemplazar en CSS (url())
            htmlContent = htmlContent.replace(
              new RegExp(`url\\(["']?(${pattern})["']?\\)`, 'gi'),
              (match, originalPath) => {
                if (/^(https?:)?\/\//.test(originalPath)) {
                  return match;
                }
                return `url("${blobUrl}")`;
              }
            );
          });
        });

        // C. Limpiar atributos problemáticos de Elementor
        htmlContent = htmlContent
          .replace(/<img([^>]*) srcset=["'][^"']*["']/gi, '<img$1')
          .replace(/<img([^>]*) sizes=["'][^"']*["']/gi, '<img$1')
          .replace(/<img([^>]*) loading=["'][^"']*["']/gi, '<img$1')
          .replace(/<img([^>]*) decoding=["'][^"']*["']/gi, '<img$1');

        // 5. Inyectar contenido en el iframe
        if (iframeRef.current) {
          const iframe = iframeRef.current;
          iframe.srcdoc = htmlContent;
          
          iframe.onload = () => {
            if (iframe.contentDocument && isMountedRef.current) {
              // Asegurar que el CSS de ocultación esté presente
              if (!iframe.contentDocument.getElementById('custom-hide-elements')) {
                const style = iframe.contentDocument.createElement('style');
                style.id = 'custom-hide-elements';
                style.textContent = hideElementsCSS;
                iframe.contentDocument.head.appendChild(style);
              }
            }
          };
        }

      } catch (err: any) {
        if (err.name !== 'AbortError' && isMountedRef.current) {
          setError(`Error al cargar: ${err.message}`);
          console.error('Error:', err);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadZipAndCreateVirtualFs();

    // Limpieza
    return () => {
      isMountedRef.current = false;
      abortController.abort();
      Object.values(virtualFsRef.current).forEach(URL.revokeObjectURL);
    };
  }, [fileUrl, hideElementsCSS]);

  // Función auxiliar para escapar strings en regex
  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  if (loading) return <CircularProgress />;
  if (error) return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;

  return (
    <iframe
      ref={iframeRef}
      style={{
        width: '100%',
        height: '80vh',
        border: 'none',
        backgroundColor: 'white'
      }}
      sandbox="allow-same-origin allow-scripts allow-popups"
      title="HTML Viewer"
    />
  );
};

export default HtmlZipViewer;